import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { 
    Plus, 
    Edit, 
    Trash2, 
    Tag, 
    Image as ImageIcon,
    Package,
    Calendar,
    GripVertical,
    Eye,
    Star,
    Upload,
    X,
    ChevronUp,
    ChevronDown,
    Search,
    Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminPromos = () => {
    const { token } = useAuth();
    const [promos, setPromos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editingPromo, setEditingPromo] = useState(null);
    const [saving, setSaving] = useState(false);
    const [activeTab, setActiveTab] = useState('info');
    
    // Form state
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        display_mode: 'featured',
        is_active: true,
        pricing_tiers: { "1": 0, "5": 0, "10": 0 }
    });
    
    // Product selection
    const [products, setProducts] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [productSearch, setProductSearch] = useState('');
    const [loadingProducts, setLoadingProducts] = useState(false);
    
    // Photo upload
    const [uploadingPhoto, setUploadingPhoto] = useState(false);

    const fetchPromos = useCallback(async () => {
        try {
            const response = await axios.get(`${API_URL}/admin/promos`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPromos(response.data.promos || []);
        } catch (error) {
            console.error('Fetch promos error:', error);
            toast.error('Erreur lors du chargement des promos');
        } finally {
            setLoading(false);
        }
    }, [token]);

    const fetchProducts = useCallback(async (search = '') => {
        setLoadingProducts(true);
        try {
            const response = await axios.get(`${API_URL}/admin/products`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { search, limit: 100 }
            });
            setProducts(response.data.products || []);
        } catch (error) {
            console.error('Fetch products error:', error);
        } finally {
            setLoadingProducts(false);
        }
    }, [token]);

    useEffect(() => {
        fetchPromos();
    }, [fetchPromos]);

    const openCreateModal = () => {
        setEditingPromo(null);
        setFormData({
            name: '',
            description: '',
            start_date: '',
            end_date: '',
            display_mode: 'featured',
            is_active: true,
            pricing_tiers: { "1": 50, "5": 45, "10": 40 }
        });
        setSelectedProducts([]);
        setActiveTab('info');
        setModalOpen(true);
        fetchProducts();
    };

    const openEditModal = async (promo) => {
        setEditingPromo(promo);
        setFormData({
            name: promo.name || '',
            description: promo.description || '',
            start_date: promo.start_date ? promo.start_date.split('T')[0] : '',
            end_date: promo.end_date ? promo.end_date.split('T')[0] : '',
            display_mode: promo.display_mode || 'featured',
            is_active: promo.is_active ?? true,
            pricing_tiers: promo.pricing_tiers || { "1": 50, "5": 45, "10": 40 }
        });
        setSelectedProducts(promo.product_ids || []);
        setActiveTab('info');
        setModalOpen(true);
        fetchProducts();
    };

    const handleSave = async () => {
        if (!formData.name) {
            toast.error('Le nom est requis');
            return;
        }

        setSaving(true);
        try {
            let promoId = editingPromo?.id;
            
            if (editingPromo) {
                // Update existing
                await axios.put(
                    `${API_URL}/admin/promos/${editingPromo.id}`,
                    {
                        ...formData,
                        start_date: formData.start_date || null,
                        end_date: formData.end_date || null
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            } else {
                // Create new
                const response = await axios.post(
                    `${API_URL}/admin/promos`,
                    {
                        ...formData,
                        start_date: formData.start_date || null,
                        end_date: formData.end_date || null
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                promoId = response.data.promo.id;
            }

            // Update products
            if (promoId) {
                const currentProducts = editingPromo?.product_ids || [];
                const toAdd = selectedProducts.filter(id => !currentProducts.includes(id));
                const toRemove = currentProducts.filter(id => !selectedProducts.includes(id));

                if (toAdd.length > 0) {
                    await axios.post(
                        `${API_URL}/admin/promos/${promoId}/products`,
                        toAdd,
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                }

                if (toRemove.length > 0) {
                    await axios.delete(
                        `${API_URL}/admin/promos/${promoId}/products`,
                        { 
                            headers: { Authorization: `Bearer ${token}` },
                            data: toRemove
                        }
                    );
                }
            }

            toast.success(editingPromo ? 'Promo mise à jour' : 'Promo créée');
            setModalOpen(false);
            fetchPromos();
        } catch (error) {
            console.error('Save promo error:', error);
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (promo) => {
        if (!window.confirm(`Supprimer la promo "${promo.name}" ?`)) return;

        try {
            await axios.delete(`${API_URL}/admin/promos/${promo.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Promo supprimée');
            fetchPromos();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const handlePhotoUpload = async (e) => {
        if (!editingPromo) return;
        
        const file = e.target.files[0];
        if (!file) return;

        setUploadingPhoto(true);
        try {
            const formData = new FormData();
            formData.append('file', file);

            const response = await axios.post(
                `${API_URL}/admin/promos/${editingPromo.id}/photos`,
                formData,
                { 
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    } 
                }
            );

            // Update local state
            setEditingPromo(prev => ({
                ...prev,
                photos: [...(prev.photos || []), response.data.url]
            }));
            
            toast.success('Photo ajoutée');
        } catch (error) {
            toast.error('Erreur lors de l\'upload');
        } finally {
            setUploadingPhoto(false);
        }
    };

    const handlePhotoDelete = async (photoUrl) => {
        if (!editingPromo) return;

        try {
            await axios.delete(
                `${API_URL}/admin/promos/${editingPromo.id}/photos`,
                { 
                    headers: { Authorization: `Bearer ${token}` },
                    params: { photo_url: photoUrl }
                }
            );

            setEditingPromo(prev => ({
                ...prev,
                photos: prev.photos.filter(p => p !== photoUrl)
            }));
            
            toast.success('Photo supprimée');
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const movePromo = async (promo, direction) => {
        const currentIndex = promos.findIndex(p => p.id === promo.id);
        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        
        if (newIndex < 0 || newIndex >= promos.length) return;

        const newPromos = [...promos];
        [newPromos[currentIndex], newPromos[newIndex]] = [newPromos[newIndex], newPromos[currentIndex]];
        
        const positions = newPromos.map((p, idx) => ({ id: p.id, position: idx }));

        try {
            await axios.put(
                `${API_URL}/admin/promos/reorder`,
                positions,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setPromos(newPromos);
        } catch (error) {
            toast.error('Erreur lors du réordonnancement');
        }
    };

    const toggleProductSelection = (productId) => {
        setSelectedProducts(prev => 
            prev.includes(productId) 
                ? prev.filter(id => id !== productId)
                : [...prev, productId]
        );
    };

    const filteredProducts = products.filter(p => {
        if (!productSearch) return true;
        const term = productSearch.toLowerCase();
        return (
            p.brand?.toLowerCase().includes(term) ||
            p.model?.toLowerCase().includes(term) ||
            p.imei?.includes(term)
        );
    });

    const updatePricingTier = (tier, value) => {
        setFormData(prev => ({
            ...prev,
            pricing_tiers: {
                ...prev.pricing_tiers,
                [tier]: parseFloat(value) || 0
            }
        }));
    };

    return (
        <AdminLayout>
            <div className="p-6 lg:p-8">
                {/* Header */}
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>
                            Ventes Événementielles
                        </h1>
                        <p className="text-slate-600 mt-1">
                            Gérez vos promos avec prix dégressifs
                        </p>
                    </div>
                    <Button onClick={openCreateModal} data-testid="create-promo-btn">
                        <Plus className="w-4 h-4 mr-2" />
                        Nouvelle Promo
                    </Button>
                </div>

                {/* Promos List */}
                {loading ? (
                    <div className="space-y-4">
                        {[...Array(3)].map((_, i) => (
                            <Card key={i} className="animate-pulse">
                                <CardContent className="p-6">
                                    <div className="h-6 bg-slate-200 rounded w-1/3 mb-4"></div>
                                    <div className="h-4 bg-slate-200 rounded w-1/2"></div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                ) : promos.length === 0 ? (
                    <Card className="border-dashed border-2">
                        <CardContent className="p-12 text-center">
                            <Tag className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                            <h2 className="text-xl font-semibold text-slate-900 mb-2">
                                Aucune promo créée
                            </h2>
                            <p className="text-slate-600 mb-4">
                                Créez votre première vente événementielle avec prix dégressifs
                            </p>
                            <Button onClick={openCreateModal}>
                                <Plus className="w-4 h-4 mr-2" />
                                Créer une promo
                            </Button>
                        </CardContent>
                    </Card>
                ) : (
                    <div className="space-y-4">
                        {promos.map((promo, index) => (
                            <Card 
                                key={promo.id} 
                                className={`border-l-4 ${promo.is_active ? 'border-l-green-500' : 'border-l-slate-300'}`}
                            >
                                <CardContent className="p-6">
                                    <div className="flex items-start justify-between">
                                        <div className="flex items-start gap-4">
                                            {/* Reorder buttons */}
                                            <div className="flex flex-col gap-1">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => movePromo(promo, 'up')}
                                                    disabled={index === 0}
                                                    className="h-6 w-6 p-0"
                                                >
                                                    <ChevronUp className="w-4 h-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => movePromo(promo, 'down')}
                                                    disabled={index === promos.length - 1}
                                                    className="h-6 w-6 p-0"
                                                >
                                                    <ChevronDown className="w-4 h-4" />
                                                </Button>
                                            </div>

                                            <div>
                                                <div className="flex items-center gap-3 mb-2">
                                                    <h3 className="text-lg font-semibold text-slate-900">
                                                        {promo.name}
                                                    </h3>
                                                    {promo.is_active ? (
                                                        <Badge className="bg-green-100 text-green-700">Active</Badge>
                                                    ) : (
                                                        <Badge variant="secondary">Inactive</Badge>
                                                    )}
                                                    {promo.display_mode === 'featured' ? (
                                                        <Badge className="bg-amber-100 text-amber-700">
                                                            <Star className="w-3 h-3 mr-1" />
                                                            Mise en avant
                                                        </Badge>
                                                    ) : (
                                                        <Badge variant="outline">
                                                            <Tag className="w-3 h-3 mr-1" />
                                                            Badge
                                                        </Badge>
                                                    )}
                                                </div>

                                                {promo.description && (
                                                    <p className="text-slate-600 text-sm mb-3">{promo.description}</p>
                                                )}

                                                {/* Stats */}
                                                <div className="flex items-center gap-6 text-sm">
                                                    <div className="flex items-center gap-2 text-slate-600">
                                                        <Package className="w-4 h-4" />
                                                        <span>{promo.available_count || 0} / {promo.product_count || 0} dispo</span>
                                                    </div>
                                                    
                                                    {promo.photos?.length > 0 && (
                                                        <div className="flex items-center gap-2 text-slate-600">
                                                            <ImageIcon className="w-4 h-4" />
                                                            <span>{promo.photos.length} photo(s)</span>
                                                        </div>
                                                    )}

                                                    {(promo.start_date || promo.end_date) && (
                                                        <div className="flex items-center gap-2 text-slate-600">
                                                            <Calendar className="w-4 h-4" />
                                                            <span>
                                                                {promo.start_date ? new Date(promo.start_date).toLocaleDateString('fr-FR') : 'Début immédiat'}
                                                                {' → '}
                                                                {promo.end_date ? new Date(promo.end_date).toLocaleDateString('fr-FR') : 'Sans limite'}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Pricing Tiers Preview */}
                                                {promo.pricing_tiers && (
                                                    <div className="flex gap-2 mt-3">
                                                        {Object.entries(promo.pricing_tiers)
                                                            .sort(([a], [b]) => parseInt(a) - parseInt(b))
                                                            .map(([qty, price]) => (
                                                                <span 
                                                                    key={qty} 
                                                                    className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full"
                                                                >
                                                                    {qty === '1' ? `${price}€/pc` : `≥${qty}pc: ${price}€`}
                                                                </span>
                                                            ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Actions */}
                                        <div className="flex items-center gap-2">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => openEditModal(promo)}
                                            >
                                                <Edit className="w-4 h-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-red-600 hover:text-red-700"
                                                onClick={() => handleDelete(promo)}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}

                {/* Create/Edit Modal */}
                <Dialog open={modalOpen} onOpenChange={setModalOpen}>
                    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>
                                {editingPromo ? 'Modifier la promo' : 'Nouvelle promo'}
                            </DialogTitle>
                        </DialogHeader>

                        <Tabs value={activeTab} onValueChange={setActiveTab}>
                            <TabsList className="mb-4">
                                <TabsTrigger value="info">Informations</TabsTrigger>
                                <TabsTrigger value="products">
                                    Produits ({selectedProducts.length})
                                </TabsTrigger>
                                {editingPromo && (
                                    <TabsTrigger value="photos">
                                        Photos ({editingPromo.photos?.length || 0})
                                    </TabsTrigger>
                                )}
                            </TabsList>

                            {/* Info Tab */}
                            <TabsContent value="info" className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="col-span-2">
                                        <Label>Nom de la promo *</Label>
                                        <Input
                                            value={formData.name}
                                            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                                            placeholder="Ex: Lot iPhone 12 - Semaine Flash"
                                        />
                                    </div>

                                    <div className="col-span-2">
                                        <Label>Description</Label>
                                        <Textarea
                                            value={formData.description}
                                            onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                                            placeholder="Description de la promo..."
                                            rows={2}
                                        />
                                    </div>

                                    <div>
                                        <Label>Date de début (optionnel)</Label>
                                        <Input
                                            type="date"
                                            value={formData.start_date}
                                            onChange={(e) => setFormData(prev => ({ ...prev, start_date: e.target.value }))}
                                        />
                                    </div>

                                    <div>
                                        <Label>Date de fin (optionnel)</Label>
                                        <Input
                                            type="date"
                                            value={formData.end_date}
                                            onChange={(e) => setFormData(prev => ({ ...prev, end_date: e.target.value }))}
                                        />
                                    </div>
                                </div>

                                {/* Pricing Tiers */}
                                <div>
                                    <Label className="mb-2 block">Prix dégressifs</Label>
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="p-4 border rounded-lg bg-slate-50">
                                            <p className="text-sm text-slate-600 mb-2">Prix unitaire</p>
                                            <div className="flex items-center gap-2">
                                                <Input
                                                    type="number"
                                                    value={formData.pricing_tiers["1"] || ''}
                                                    onChange={(e) => updatePricingTier("1", e.target.value)}
                                                    className="w-24"
                                                />
                                                <span className="text-slate-600">€</span>
                                            </div>
                                        </div>
                                        <div className="p-4 border rounded-lg bg-blue-50">
                                            <p className="text-sm text-blue-600 mb-2">À partir de 5 pcs</p>
                                            <div className="flex items-center gap-2">
                                                <Input
                                                    type="number"
                                                    value={formData.pricing_tiers["5"] || ''}
                                                    onChange={(e) => updatePricingTier("5", e.target.value)}
                                                    className="w-24"
                                                />
                                                <span className="text-slate-600">€/pc</span>
                                            </div>
                                        </div>
                                        <div className="p-4 border rounded-lg bg-green-50">
                                            <p className="text-sm text-green-600 mb-2">À partir de 10 pcs</p>
                                            <div className="flex items-center gap-2">
                                                <Input
                                                    type="number"
                                                    value={formData.pricing_tiers["10"] || ''}
                                                    onChange={(e) => updatePricingTier("10", e.target.value)}
                                                    className="w-24"
                                                />
                                                <span className="text-slate-600">€/pc</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Display Mode */}
                                <div>
                                    <Label className="mb-2 block">Mode d'affichage</Label>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div 
                                            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                                formData.display_mode === 'featured' 
                                                    ? 'border-amber-500 bg-amber-50' 
                                                    : 'border-slate-200 hover:border-slate-300'
                                            }`}
                                            onClick={() => setFormData(prev => ({ ...prev, display_mode: 'featured' }))}
                                        >
                                            <div className="flex items-center gap-2 mb-2">
                                                <Star className="w-5 h-5 text-amber-500" />
                                                <span className="font-medium">Mise en avant</span>
                                            </div>
                                            <p className="text-sm text-slate-600">
                                                Bandeau visible en haut du catalogue avec photos et prix
                                            </p>
                                        </div>
                                        <div 
                                            className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                                                formData.display_mode === 'badge' 
                                                    ? 'border-blue-500 bg-blue-50' 
                                                    : 'border-slate-200 hover:border-slate-300'
                                            }`}
                                            onClick={() => setFormData(prev => ({ ...prev, display_mode: 'badge' }))}
                                        >
                                            <div className="flex items-center gap-2 mb-2">
                                                <Tag className="w-5 h-5 text-blue-500" />
                                                <span className="font-medium">Badge discret</span>
                                            </div>
                                            <p className="text-sm text-slate-600">
                                                Simple badge "PROMO" sur les produits concernés
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* Active Toggle */}
                                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                                    <div>
                                        <p className="font-medium">Promo active</p>
                                        <p className="text-sm text-slate-600">Visible par les clients</p>
                                    </div>
                                    <Switch
                                        checked={formData.is_active}
                                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                                    />
                                </div>
                            </TabsContent>

                            {/* Products Tab */}
                            <TabsContent value="products" className="space-y-4">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                                    <Input
                                        value={productSearch}
                                        onChange={(e) => setProductSearch(e.target.value)}
                                        placeholder="Rechercher produits..."
                                        className="pl-10"
                                    />
                                </div>

                                <div className="border rounded-lg max-h-96 overflow-y-auto">
                                    {loadingProducts ? (
                                        <div className="p-8 text-center text-slate-500">
                                            Chargement...
                                        </div>
                                    ) : filteredProducts.length === 0 ? (
                                        <div className="p-8 text-center text-slate-500">
                                            Aucun produit trouvé
                                        </div>
                                    ) : (
                                        <div className="divide-y">
                                            {filteredProducts.map((product) => (
                                                <div
                                                    key={product.id}
                                                    className={`p-3 flex items-center gap-4 cursor-pointer hover:bg-slate-50 ${
                                                        selectedProducts.includes(product.id) ? 'bg-blue-50' : ''
                                                    }`}
                                                    onClick={() => toggleProductSelection(product.id)}
                                                >
                                                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                                        selectedProducts.includes(product.id) 
                                                            ? 'bg-blue-500 border-blue-500' 
                                                            : 'border-slate-300'
                                                    }`}>
                                                        {selectedProducts.includes(product.id) && (
                                                            <Check className="w-3 h-3 text-white" />
                                                        )}
                                                    </div>
                                                    <div className="flex-1">
                                                        <p className="font-medium">{product.brand} {product.model}</p>
                                                        <p className="text-sm text-slate-500">
                                                            {product.storage_capacity}GB • Grade {product.grade} • {product.imei}
                                                        </p>
                                                    </div>
                                                    <p className="font-bold">{product.price}€</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <p className="text-sm text-slate-600">
                                    {selectedProducts.length} produit(s) sélectionné(s)
                                </p>
                            </TabsContent>

                            {/* Photos Tab */}
                            {editingPromo && (
                                <TabsContent value="photos" className="space-y-4">
                                    <div className="grid grid-cols-4 gap-4">
                                        {editingPromo.photos?.map((photo, index) => (
                                            <div key={index} className="relative group">
                                                <img
                                                    src={photo}
                                                    alt={`Promo ${index + 1}`}
                                                    className="w-full h-32 object-cover rounded-lg"
                                                />
                                                <button
                                                    onClick={() => handlePhotoDelete(photo)}
                                                    className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                                >
                                                    <X className="w-4 h-4" />
                                                </button>
                                            </div>
                                        ))}

                                        {/* Upload button */}
                                        <label className="border-2 border-dashed border-slate-300 rounded-lg h-32 flex flex-col items-center justify-center cursor-pointer hover:border-slate-400 transition-colors">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handlePhotoUpload}
                                                className="hidden"
                                                disabled={uploadingPhoto}
                                            />
                                            {uploadingPhoto ? (
                                                <span className="text-slate-500">Upload...</span>
                                            ) : (
                                                <>
                                                    <Upload className="w-8 h-8 text-slate-400 mb-2" />
                                                    <span className="text-sm text-slate-500">Ajouter</span>
                                                </>
                                            )}
                                        </label>
                                    </div>

                                    <p className="text-sm text-slate-600">
                                        Les photos seront affichées dans le bandeau de la promo
                                    </p>
                                </TabsContent>
                            )}
                        </Tabs>

                        <DialogFooter>
                            <Button variant="outline" onClick={() => setModalOpen(false)}>
                                Annuler
                            </Button>
                            <Button onClick={handleSave} disabled={saving}>
                                {saving ? 'Enregistrement...' : 'Enregistrer'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>
        </AdminLayout>
    );
};

export default AdminPromos;
